import { useState, useContext, useEffect } from 'react';
import {
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import { InputButton, InputTextField } from '@abbott-components/core';
import { useSnackbar } from 'notistack';
import { post } from '../Shared/ajax';
import { WorkflowEngineContext } from '../WorkflowEngine/workflowEngineReducer';
import { getErrorMessage } from '../Shared/errorHandling';
import { AppContext } from '../App/appReducer';
import { AppActionKind, AppPageKind } from '../App/types';

interface PublishWorkflowResponse {
  dynamicWorkflowVersionExternalId: string;
}

interface PublishWorkflowRequest {
  dynamicWorkflowVersionExternalId: string;
  releaseNotes: string;
}

const PublishDialog = ({
  handleClose,
  open
}: {
  handleClose: () => void;
  open: boolean;
}) => {
  const [releaseNotes, setReleaseNotes] = useState<string>('');
  const [notesError, setNotesError] = useState<boolean>(false);
  const [releaseDate, setReleaseDate] = useState<string>('');

  const { state } = useContext(WorkflowEngineContext);
  const { dispatch: appDispatch } = useContext(AppContext);
  const { isReadOnlyView, versionExternalId, versionData, isPaused } = state;
  const { enqueueSnackbar } = useSnackbar();

  const handleInputText = (input: string) => {
    if (isReadOnlyView) return;

    if (input.length <= 10000) {
      setReleaseNotes(input);
      setNotesError(false);
    } else {
      setNotesError(true);
    }
  };

  const handlePublish = async () => {
    if (notesError) {
      return;
    }
    try {
      await post<PublishWorkflowResponse, PublishWorkflowRequest>(
        `DynamicWorkflow/PublishWorkflowVersion`,
        {
          dynamicWorkflowVersionExternalId: versionExternalId,
          releaseNotes: releaseNotes
        }
      );

      enqueueSnackbar('Workflow successfully published!', {
        variant: 'success'
      });
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }

    appDispatch({
      type: AppActionKind.SET_APPPAGE,
      payload: AppPageKind.LANDING
    });
  };

  useEffect(() => {
    if (isReadOnlyView && versionData) {
      setReleaseNotes(versionData.releaseNotes);
      const utcDate = new Date(versionData.datePublished);
      setReleaseDate(utcDate.toLocaleString());
    }
  }, [isReadOnlyView, versionData]);

  return (
    <Dialog onClose={handleClose} open={open} fullWidth={true} maxWidth={'sm'}>
      <DialogTitle
        sx={{ textAlign: 'center', fontSize: '20px', fontWeight: 'bolder' }}
      >
        {isReadOnlyView ? 'Release Notes' : 'Enter Publishing Notes'}
      </DialogTitle>
      <DialogContent>
        <Stack>
          {isReadOnlyView && (
            <>
              <Typography variant="subtitle1">{`Published by: ${versionData?.publishedBy}`}</Typography>
              <Typography variant="overline">{`Version: ${versionData?.version}`}</Typography>
              <Typography variant="inherit">{`Published on: ${releaseDate}`}</Typography>
            </>
          )}
          <InputTextField
            name="releaseNotes"
            placeholder="Enter your release notes here..."
            multiline
            minRows={6}
            value={releaseNotes}
            onTextChange={handleInputText}
            error={notesError}
            errorText={
              notesError
                ? 'Release Notes must be less than 10,000 characters'
                : undefined
            }
          />
          {isPaused && (
            <Typography
              sx={{ fontStyle: 'italic', fontSize: 14, textAlign: 'center' }}
            >
              This workflow is currently paused. Publishing an update will
              unpause it.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {isReadOnlyView ? (
          <InputButton
            name="cancelPublish"
            type="button"
            variant="text"
            onClick={handleClose}
          >
            Close
          </InputButton>
        ) : (
          <>
            <InputButton
              name="cancelPublish"
              type="button"
              variant="text"
              onClick={handleClose}
            >
              Cancel
            </InputButton>
            <InputButton
              name="confirmPublish"
              type="button"
              variant="contained"
              onClick={handlePublish}
            >
              Confirm
            </InputButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PublishDialog;
